<template>
  <div>
    <CollectionView searchable :endpoint="utilities.path('modules/wiki/categories/@/articles', categoryId)" v-model:reload="reload">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h4 class="mb-0">{{ translate("articles") }}</h4>
          <ButtonView v-if="canModifyArticles" type="normal" color="primary" @click="creating = true">
            {{ translate("create") }}
          </ButtonView>
        </div>
      </template>
      <template v-slot:content="{ items }">
        <div class="card">
          <div class="table-responsive">
            <div class="table">
              <div class="table-header">
                <div class="table-header-cell">{{ translate("name") }}</div>
                <div class="table-header-cell">{{ translate("published") }}</div>
                <div class="table-header-cell">{{ translate("created_at") }}</div>
                <div class="table-header-cell">{{ translate("updated_at") }}</div>
                <div class="table-header-cell">{{ translate("actions") }}</div>
              </div>
              <div class="table-body">
                <div class="table-row" v-for="item in items" :key="item.id">
                  <div class="table-column">{{ item.name }}</div>
                  <div class="table-column">
                    <i class="fas fa-fw align-middle" style="font-size: 1.1em" :class="item.published ? 'fa-check text-success' : 'fa-xmark text-warning'"></i>
                  </div>
                  <div class="table-column">{{ utilities.formatDatetime(item.createdAt) }}</div>
                  <div class="table-column">{{ item.updatedAt ? utilities.formatDatetime(item.updatedAt) : "" }}</div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <ButtonView v-if="canModifyArticles" type="soft" color="primary" size="small" @click="editId = item.id">
                        <i class="fas fa-edit"></i>
                      </ButtonView>
                      <TargetedModalHttpFormView v-if="canModifyArticles" size="small" :endpoint="utilities.path('modules/wiki/articles/@', item.id)" method="delete" @success="reload = true">
                        <template v-slot:target="{ show }">
                          <ButtonView type="soft" color="danger" size="small" @click="show">
                            <i class="fas fa-trash"></i>
                          </ButtonView>
                        </template>
                        <template v-slot:form="{ submit, loading, hide }">
                          <h2 class="mb-4">{{ item.name }}</h2>
                          <SpinnerView :active="loading" color="danger"/>
                          <div class="d-flex align-items-center g-4">
                            <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                              {{ translate("delete") }}
                            </ButtonView>
                            <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                              {{ translate("cancel") }}
                            </ButtonView>
                          </div>
                        </template>
                      </TargetedModalHttpFormView>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CollectionView>
    <ModalView size="xlarge" :active="creating || editId !== undefined" @update:active="tryClose">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between g-4">
          <h3 class="mb-0">{{ translate("article") }}</h3>
          <ModalCloseButtonView @click="tryClose"/>
        </div>
      </template>
      <template v-slot:body>
        <HttpContentView v-if="editId" :endpoint="utilities.path('modules/wiki/articles/@', editId)">
          <template v-slot:content="{ data }">
            <WikiArticleFormView :category-id="categoryId" :article="data" v-model:changes="changes" @success="editId = undefined; reload = true" @cancel="tryClose"/>
          </template>
        </HttpContentView>
        <WikiArticleFormView v-else :category-id="categoryId" v-model:changes="changes" @success="creating = false; reload = true" @cancel="tryClose"/>
      </template>
    </ModalView>
    <ModalView size="small" v-model:active="discardMenu">
      <template v-slot:body>
        {{ translate("form.unsaved.changes.description") }}
        <div class="d-flex align-items-center g-4 mt-4">
          <ButtonView type="normal" color="danger" class="w-100" @click="creating = false; editId = undefined; discardMenu = false">
            {{ translate("discard.changes") }}
          </ButtonView>
          <ButtonView type="normal" color="primary" class="w-100" @click="discardMenu = false">
            {{ translate("continue") }}
          </ButtonView>
        </div>
      </template>
    </ModalView>
  </div>
</template>

<script setup>
import {CollectionView, utilities, ButtonView, SpinnerView, TargetedModalHttpFormView, ModalView, HttpContentView, ModalCloseButtonView} from "@/niftybit";
import {computed, ref} from "vue";
import authorization from "@/authorization";
import WikiArticleFormView from "@/systems/modules/wiki/WikiArticleFormView.vue";
import BlogPostFormView from "@/systems/modules/blog/BlogPostFormView.vue";

const props = defineProps({
  communityId: String,
  categoryId: String
});

const reload = ref(false);
const creating = ref(false);
const editId = utilities.routeQueryParam("editId");

const changes = ref(false);
const discardMenu = ref(false);

const canModifyArticles = computed(() => authorization.permission("modules.wiki.articles.edit") || authorization.resourcePermission("modules.wiki.category", props.categoryId, "articles.edit"));

function tryClose() {
  if (changes.value) {
    discardMenu.value = true;
  } else {
    creating.value = false;
    editId.value = undefined;
  }
}

</script>